import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'
import { useAuthStore } from '../../stores/authStore'

type PaginatedResponse<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

type SessionListItem = {
  id: number
  client: number
  client_name: string
  service_date: string
  service_code: string
  modifiers: string[]
  scheduled_start: string
  scheduled_end: string
  actual_start: string | null
  actual_end: string | null
  status: string
  status_display: string
  rendering_provider_name: string
  place_of_service: string
  place_of_service_display: string
  billable_units: string | null
  duration_minutes: number
  billing_status: string
  billing_status_display: string
  has_note: boolean
  note_status: string
  created_at: string
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No show' },
  { value: 'late_cancel', label: 'Late cancel' },
]

export default function SessionsListPage() {
  const tenantSlug = resolveTenantSlug()
  const user = useAuthStore((s) => s.user)
  const [searchParams, setSearchParams] = useSearchParams()

  const initialClient = searchParams.get('client') ?? ''
  const initialPendingNotes = searchParams.get('pending_notes') === 'true'

  const [search, setSearch] = useState(searchParams.get('search') ?? '')
  const [status, setStatus] = useState(searchParams.get('status') ?? '')
  const [client, setClient] = useState(initialClient)
  const [pendingNotes, setPendingNotes] = useState(initialPendingNotes)
  const [startDate, setStartDate] = useState(searchParams.get('start_date') ?? '')
  const [endDate, setEndDate] = useState(searchParams.get('end_date') ?? '')

  const params = useMemo(() => {
    const p: Record<string, string> = {}
    if (search.trim()) p.search = search.trim()
    if (status) p.status = status
    if (client) p.client = client
    if (startDate) p.start_date = startDate
    if (endDate) p.end_date = endDate
    if (pendingNotes) p.pending_notes = 'true'
    return p
  }, [client, endDate, pendingNotes, search, startDate, status])

  const cacheKey = useMemo(() => {
    const q = search.trim().toLowerCase()
    return `tenant:${tenantSlug ?? 'unknown'}:sessions:list:status=${status || 'all'}:q=${q || 'all'}:client=${client || 'all'}:start=${startDate || 'all'}:end=${endDate || 'all'}:pending=${pendingNotes ? '1' : '0'}`
  }, [client, endDate, pendingNotes, search, startDate, status, tenantSlug])

  const query = useOfflineFirstQuery<PaginatedResponse<SessionListItem>>({
    queryKey: ['sessions', params],
    cacheKey,
    cacheMaxAgeMs: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<PaginatedResponse<SessionListItem>>('/api/v1/sessions/', {
        params,
      })
      return res.data
    },
  })

  const rows = query.data?.results ?? []
  const canCreateSession = user?.role === 'admin' || user?.role === 'bcba' || user?.role === 'rbt'

  const applyToUrl = () => {
    const next = new URLSearchParams()
    if (search.trim()) next.set('search', search.trim())
    if (status) next.set('status', status)
    if (client) next.set('client', client)
    if (startDate) next.set('start_date', startDate)
    if (endDate) next.set('end_date', endDate)
    if (pendingNotes) next.set('pending_notes', 'true')
    setSearchParams(next, { replace: true })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sessions</h1>
          <p className="text-sm text-gray-600">Clock in/out, note completion, billing readiness.</p>
        </div>
        {canCreateSession && (
          <Link to="/app/sessions/new" className="btn-primary">
            New session
          </Link>
        )}
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className="label" htmlFor="session-search">
              Search
            </label>
            <input
              id="session-search"
              className="input mt-1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Client name or MRN"
            />
          </div>

          <div>
            <label className="label" htmlFor="session-status">
              Status
            </label>
            <select
              id="session-status"
              className="input mt-1"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label" htmlFor="start-date">
              Start
            </label>
            <input id="start-date" type="date" className="input mt-1" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>

          <div>
            <label className="label" htmlFor="end-date">
              End
            </label>
            <input id="end-date" type="date" className="input mt-1" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>

          <div className="flex items-end">
            <button type="button" className="btn-secondary w-full" onClick={applyToUrl}>
              Apply
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={pendingNotes}
                onChange={(e) => setPendingNotes(e.target.checked)}
              />
              Pending notes only
            </label>
            <div>
              <label className="sr-only" htmlFor="client-id">
                Client ID
              </label>
              <input
                id="client-id"
                className="input"
                value={client}
                onChange={(e) => setClient(e.target.value)}
                placeholder="Client ID"
              />
            </div>
          </div>

          <button
            type="button"
            className="text-sm font-medium text-gray-700 hover:text-gray-900"
            onClick={() => {
              setSearch('')
              setStatus('')
              setClient('')
              setStartDate('')
              setEndDate('')
              setPendingNotes(false)
              setSearchParams(new URLSearchParams(), { replace: true })
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {query.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(query.error)}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Date</th>
                <th className="table-header">Client</th>
                <th className="table-header">Service</th>
                <th className="table-header">Status</th>
                <th className="table-header">Note</th>
                <th className="table-header">Billing</th>
                <th className="table-header">Units</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {query.isLoading && rows.length === 0 ? (
                Array.from({ length: 10 }).map((_, idx) => (
                  <tr key={idx}>
                    <td className="table-cell"><div className="h-4 w-20 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-44 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-16 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-20 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-16 bg-gray-200 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="table-cell text-gray-500">
                    No sessions found.
                  </td>
                </tr>
              ) : (
                rows.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <Link to={`/app/sessions/${s.id}`} className="font-medium text-gray-900 hover:text-primary-600">
                        {s.service_date}
                      </Link>
                    </td>
                    <td className="table-cell">{s.client_name}</td>
                    <td className="table-cell">{s.service_code}</td>
                    <td className="table-cell">{s.status_display ?? s.status}</td>
                    <td className="table-cell">
                      {s.note_status === 'not_started' ? (
                        <span className="inline-flex items-center rounded-md bg-red-50 border border-red-200 px-2 py-1 text-xs font-medium text-red-800">
                          Missing
                        </span>
                      ) : s.note_status === 'locked' ? (
                        <span className="inline-flex items-center rounded-md bg-green-50 border border-green-200 px-2 py-1 text-xs font-medium text-green-800">
                          Locked
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-xs font-medium text-amber-800">
                          {s.note_status.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="table-cell">{s.billing_status_display ?? s.billing_status}</td>
                    <td className="table-cell">{s.billable_units ?? '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

